import React, { useEffect, useState } from 'react';
import { Box, Stack, Tab, Tabs } from '@mui/material';
import { AssetIssues } from 'types/room';
import {
  ContentExtraTableValidation,
  FormEditSubmittedEntries
} from '../../elements/ContentExtraTableValidation';
import { EditAssetForm, TypeEditAssetForm } from '../../elements/EditAssetForm';
import { TableReportTabAssetValidation } from '../../elements/TableReport';
import { DropdownOption } from 'app/pages/RoomReportViewPhotoPage/hook';
import { ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';
import { TypeDocumentOption } from 'hooks/useGetDocumentOption';
import { FormAddDocument } from 'types/documents';

const EXPAND_PANEL_MAX_WIDTH_PX = 800;

type AssetValidationExpandedContentProps = {
  rowActive: AssetIssues | undefined;
  mapOptions: Map<string, DropdownOption>;
  options: ListOptions;
  documentOptions: TypeDocumentOption[];
  modelFiles: TypeDocumentOption[];
  issuesOptions: Record<string, string>;
  onEditSubmittedEntries: (payload: {
    value: FormEditSubmittedEntries;
    rowActive: AssetIssues;
  }) => void;
  onSubmitAsset: (data: TypeEditAssetForm) => void;
  asyncAddDocument: (value: FormAddDocument) => void;
};

export const AssetValidationExpandedContent = ({
  rowActive,
  mapOptions,
  options,
  documentOptions,
  modelFiles,
  issuesOptions,
  onEditSubmittedEntries,
  onSubmitAsset,
  asyncAddDocument
}: AssetValidationExpandedContentProps) => {
  const [tab, setTab] = useState(0);

  useEffect(() => {
    setTab(0);
  }, [rowActive?.id]);

  if (!rowActive) {
    return null;
  }

  return (
    <Stack width="100%" alignItems="flex-start" gap={0}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          minHeight: 44,
          alignSelf: 'stretch',
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            fontWeight: 700,
            fontSize: '15px',
            color: '#5F5F5F',
            textTransform: 'none'
          },
          '& .Mui-selected': { color: '#226AAA' },
          '& .MuiTabs-indicator': { backgroundColor: '#226AAA' }
        }}>
        <Tab label="Attributes" />
        <Tab label="Properties" />
        <Tab label="Issues" />
      </Tabs>

      {tab === 2 ? (
        <Box
          sx={{ pt: 2.5, width: '100%', alignSelf: 'stretch', maxWidth: '100%', minHeight: 500 }}>
          <TableReportTabAssetValidation
            rowActive={rowActive}
            issues={rowActive.issueReports || []}
            mapOptions={mapOptions}
          />
        </Box>
      ) : (
        <Box
          sx={{
            pt: 2.5,
            width: '100%',
            maxWidth: EXPAND_PANEL_MAX_WIDTH_PX,
            alignSelf: 'stretch'
          }}>
          {tab === 0 && (
            <EditAssetForm
              clientId={rowActive?.room?.clientId?.toString()}
              options={options}
              existingPhotos={
                rowActive?.photos
                  ?.filter((photo) => photo.id != null)
                  .map((photo) => ({
                    id: Number(photo.id),
                    url: photo.url,
                    storage: photo.storage
                  })) || []
              }
              initValues={{
                assetName: rowActive?.name,
                latitude: rowActive?.submitted.lat,
                assetType: rowActive?.type?.id?.toString(),
                longitude: rowActive?.submitted.long,
                validated: rowActive?.validated ? 'Yes' : 'No',
                documentOptions: documentOptions,
                modelFiles: modelFiles,
                model: rowActive?.model?.id?.toString(),
                // criticality: rowActive?.criticality?.toString(),
                criticalityKey: rowActive?.criticalityKey || '',
                manufacturerId: rowActive?.model?.manufacturer?.id?.toString(),
                options: rowActive?.options,
                assetCustomId: rowActive?.assetCustomId,
                assetCustomId2: rowActive?.assetCustomId2,
                note: rowActive?.note,
                otherNote: rowActive?.otherNote,
                lastMaintenance: rowActive?.lastMaintenance,
                entries: rowActive?.submittedEntries,
                oemAttributes: rowActive?.oemAttributes,
                maintenanceEnabled: rowActive?.maintenanceEnabled, // ← add
                maintenanceConfigs: rowActive?.model?.maintenanceConfigs || [], // ← add
                assetDevices: rowActive?.assetDevices || [], // ← add
                ...issuesOptions
              }}
              onSubmit={onSubmitAsset}
              asyncAddDocument={asyncAddDocument}
              hideDynamicAssetFields
              fullWidth
              twoColumnGridLayout
            />
          )}

          {tab === 1 && (
            <ContentExtraTableValidation
              fieldsGridLayout
              onEditSubmittedEntries={(value) => onEditSubmittedEntries({ value, rowActive })}
              dataRowActive={rowActive}
              mapOptions={mapOptions}
            />
          )}
        </Box>
      )}
    </Stack>
  );
};
